import { DriftCalculationMethod, FeatureFlagTypes } from '@generated/graphql';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import { DEFAULT_DRIFT_TOLERANCE_PERCENT } from '@pages/PortfolioTradingHub/utils/constants';
import { enumValues } from '@utils/enumValues';
import { Grid, Typography } from 'hephaestus/web';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../../hooks/usePermission';
import { useOrgDriftDefaults } from '../hooks/useOrgDriftDefaults';

/**
 * Its own component so the parent form never calls useQuery.
 *
 * The parent is otherwise a pure controlled form with no Apollo dependency,
 * and hooks cannot be called conditionally — mounting the query up there would
 * make an Apollo client mandatory for every consumer and test of the form,
 * even the ones that never show this section.
 */
export const DriftSettingsSection = ({
  organizationId,
  method,
  tolerancePercent,
  toleranceInvalid,
  loading,
  sectionLabel,
  onChange,
}: {
  organizationId: string | undefined;
  method: DriftCalculationMethod | null | undefined;
  tolerancePercent: number | null | undefined;
  toleranceInvalid: boolean;
  loading: boolean;
  sectionLabel: unknown;
  onChange: (patch: {
    driftCalculationMethod?: DriftCalculationMethod | null;
    driftTolerancePercent?: number | null;
  }) => void;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['modelPortfolios', 'shared', 'client']);
  // Org feature flag, not Flagsmith: this gates a backend capability. The
  // singular hook reads the resolved set off UserContext and carries no
  // read:feature_flags requirement of its own.
  const isDriftMethodEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.DriftCalculationMethodEnabled,
  );
  const canEditDrift = usePermission('write:drift_settings');
  const orgDefaults = useOrgDriftDefaults({
    organizationId,
    enabled: isDriftMethodEnabled,
  });

  if (!isDriftMethodEnabled) return null;

  const sectionDisabled = loading || !canEditDrift;
  // Named only once the org's defaults were actually read: the policy resolves
  // against the HOLDER org, which a viewer may not be able to read at all.
  const inheritedMethod = orgDefaults.resolved
    ? (orgDefaults.method ?? DriftCalculationMethod.Relative)
    : undefined;

  return (
    <Grid container spacing={2} sx={{ mt: 1, mb: 1 }} size={12}>
      <Grid size={12}>
        {/* Dims with the fields: on a read-only model every label and value
            around it is already muted, and a full-strength heading is the one
            thing left looking editable. */}
        <Typography
          variant='labelLarge'
          color={sectionDisabled ? 'textSecondary' : undefined}
        >
          {translateBackendString(sectionLabel as never)}
          <InfoDialog
            information={t(
              'modelPortfolios:createModelPortfolio.driftSettingsTooltip',
            )}
          />
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <SelectField
          testId='drift-calculation-method'
          label={t(
            'modelPortfolios:createModelPortfolio.driftCalculationMethodLabel',
          )}
          disabled={sectionDisabled}
          fullWidth
          // The sentinel exists only at this boundary. Storing it would put a
          // non-schema value into the mutation input.
          value={method ?? inheritedMethod ?? 'inherited'}
          // Choosing the method the model would inherit anyway clears the
          // override, so the list stays at two options.
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChange({
              driftCalculationMethod:
                e.target.value === 'inherited' ||
                e.target.value === inheritedMethod
                  ? null
                  : (e.target.value as DriftCalculationMethod),
            })
          }
        >
          {/* Keeps an unresolved inherit displayable without offering it.
              style, not sx: SelectField overwrites each child's sx. */}
          {!inheritedMethod && (
            <MenuItem value='inherited' style={{ display: 'none' }}>
              {t('modelPortfolios:createModelPortfolio.driftInheritedUnknown')}
            </MenuItem>
          )}
          {enumValues(DriftCalculationMethod).map((value) => {
            const label = t(`client:driftCalculationMethodItems.${value}`);
            return (
              <MenuItem key={value} value={value}>
                {/* Marks where choosing reverts to inheriting; a model's own
                    method is never shown as inherited. */}
                {value === inheritedMethod && method !== inheritedMethod
                  ? t('modelPortfolios:createModelPortfolio.driftInherited', {
                      method: label,
                    })
                  : label}
              </MenuItem>
            );
          })}
        </SelectField>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <NumberField
          testId='drift-tolerance-percent'
          label={t(
            'modelPortfolios:createModelPortfolio.driftTolerancePercentLabel',
          )}
          disabled={sectionDisabled}
          trailingIcon='percent'
          // Matches the org settings field: whole numbers only. Without
          // decimalPlaces={0} the field accepts 12.9, parseInt silently
          // stores 12, and the range check never sees the decimal — the user
          // continues with a different value than they typed.
          decimalPlaces={0}
          unsigned={true}
          helpText={t(
            'modelPortfolios:createModelPortfolio.driftToleranceHint',
          )}
          number={tolerancePercent?.toString() ?? ''}
          // Same rule as the method above: a placeholder is a claim about what
          // this model will actually be screened at, so it is shown only once
          // the org's defaults have been read.
          placeholder={
            orgDefaults.resolved
              ? `${orgDefaults.tolerancePercent ?? DEFAULT_DRIFT_TOLERANCE_PERCENT}`
              : undefined
          }
          error={toleranceInvalid}
          errorText={
            toleranceInvalid
              ? t(
                  'modelPortfolios:createModelPortfolio.driftToleranceRangeError',
                )
              : undefined
          }
          // decimalPlaces={0} above is what keeps fractions out: the field
          // sanitises 12.9 to 12 before this runs, so what is stored is always
          // what is displayed. parseInt matches the org settings field, and a
          // test fails if that prop is ever dropped.
          //
          // Blank means inherit and must persist as an explicit null: the
          // mutation drops undefined, so an empty string here would silently
          // skip the clear-to-inherit.
          setNumber={(next) =>
            onChange({
              driftTolerancePercent: next === '' ? null : parseInt(next, 10),
            })
          }
        />
      </Grid>
    </Grid>
  );
};
